'use client';

/* eslint-disable jsx-a11y/media-has-caption */
import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import React, { type CSSProperties, type SyntheticEvent, useRef, useState } from 'react';
import { PauseIcon } from '@/components/icons';
import { PlayIcon } from '@/components/tutorials-carrousel/tutorial-card/play-icon';
import { isNumber } from '@/util/type-guards';
import { classNames } from '@/util/utils';
import styles from './video.module.css';
interface ProgressiveVideoProps {
  className?: string;
  src: string;
  autosize?: boolean;
  controls?: boolean;
  width?: number;
  height?: number;
  currentTime?: number;
  onCurrentTimeChange?(currentTime?: number): void;
}
const DEFAULT_ASPECT_RATIO = '16/9';
function getAutosizeStyle(width?: number, height?: number): CSSProperties {
  return {
    width: '100%',
    height: 'auto',
    aspectRatio: width && height ? `${width}/${height}` : DEFAULT_ASPECT_RATIO
  };
}
export default function ProgressiveVideo(t0) {
  const $ = _c(46);
  const {
    className,
    src,
    controls: t1,
    autosize,
    width,
    height,
    currentTime,
    onCurrentTimeChange
  } = t0;
  const controls = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const isHomepage = pathname === "/";
  const [isHovered, setIsHovered] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const refVideo = useRef(null);
  let t2;
  if ($[0] !== autosize || $[1] !== height || $[2] !== width) {
    t2 = () => autosize ? getAutosizeStyle(width, height) : {};
    $[0] = autosize;
    $[1] = height;
    $[2] = width;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [style, setStyle] = useState(t2);
  let t3;
  if ($[4] !== autosize) {
    t3 = evt => {
      const video = evt.target as HTMLVideoElement;
      if (!autosize || !video) {
        return;
      }
      setStyle(getAutosizeStyle(video.videoWidth, video.videoHeight));
    };
    $[4] = autosize;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleReady = t3;
  let t4;
  let t5;
  if ($[6] !== currentTime) {
    t4 = () => {
      const video_0 = refVideo.current;
      if (!isNumber(currentTime) || !video_0) {
        return;
      }
      video_0.currentTime = currentTime;
    };
    t5 = [currentTime];
    $[6] = currentTime;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  React.useEffect(t4, t5);
  let t6;
  if ($[9] !== onCurrentTimeChange) {
    t6 = () => {
      const video_1 = refVideo.current;
      if (!video_1 || !onCurrentTimeChange) {
        return;
      }
      onCurrentTimeChange(video_1.currentTime);
    };
    $[9] = onCurrentTimeChange;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleTimeUpdate = t6;
  let t7;
  if ($[11] !== onCurrentTimeChange) {
    t7 = () => {
      const video_2 = refVideo.current;
      if (!video_2 || !onCurrentTimeChange) {
        return;
      }
      setVideoPlaying(true);
      video_2.play();
    };
    $[11] = onCurrentTimeChange;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handlePlay = t7;
  let t8;
  if ($[13] !== onCurrentTimeChange) {
    t8 = () => {
      const video_3 = refVideo.current;
      if (!video_3 || !onCurrentTimeChange) {
        return;
      }
      setVideoPlaying(false);
      video_3.pause();
    };
    $[13] = onCurrentTimeChange;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handlePause = t8;
  let t10;
  let t9;
  if ($[15] !== isHovered) {
    t9 = () => {
      const video_4 = refVideo.current;
      if (!video_4) {
        return;
      }
      video_4.muted = !isHovered;
    };
    t10 = [isHovered];
    $[15] = isHovered;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  React.useEffect(t9, t10);
  let t11;
  let t12;
  let t13;
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setIsHovered(true);
    t12 = () => setIsHovered(true);
    t13 = () => setIsHovered(false);
    t14 = () => setIsHovered(false);
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t11 = $[18];
    t12 = $[19];
    t13 = $[20];
    t14 = $[21];
  }
  let t15;
  if ($[22] !== className) {
    t15 = classNames(className, styles.video);
    $[22] = className;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== handlePause || $[25] !== handlePlay || $[26] !== isHomepage || $[27] !== isHovered || $[28] !== videoPlaying) {
    t16 = isHomepage && <>{videoPlaying ? <button type="button" aria-label="pause video" onClick={handlePause} className={styles.playButton} style={{
        opacity: isHovered ? 1 : 0
      }}><PauseIcon className="h-16 w-auto" /></button> : <button type="button" aria-label="play video" onClick={handlePlay} className={styles.playButton}><PlayIcon className="h-32 w-auto" /></button>}</>;
    $[24] = handlePause;
    $[25] = handlePlay;
    $[26] = isHomepage;
    $[27] = isHovered;
    $[28] = videoPlaying;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = controls && styles.pointer;
  let t18;
  if ($[30] !== t17) {
    t18 = classNames(t17);
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const t19 = !controls;
  const t20 = !controls && !isHomepage;
  let t21;
  if ($[32] !== controls || $[33] !== handlePlay || $[34] !== handleReady || $[35] !== handleTimeUpdate || $[36] !== src || $[37] !== t18 || $[38] !== t19 || $[39] !== t20) {
    t21 = <video className={t18} src={src} ref={refVideo} controls={controls} muted={true} loop={t19} disablePictureInPicture={true} playsInline={true} autoPlay={t20} onPlay={handlePlay} onCanPlay={handleReady} onTimeUpdate={handleTimeUpdate} />;
    $[32] = controls;
    $[33] = handlePlay;
    $[34] = handleReady;
    $[35] = handleTimeUpdate;
    $[36] = src;
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== style || $[42] !== t15 || $[43] !== t16 || $[44] !== t21) {
    t22 = <div onMouseOver={t11} onFocus={t12} onMouseOut={t13} onBlur={t14} className={t15} aria-label="Click to play" style={style}>{t16}{t21}</div>;
    $[41] = style;
    $[42] = t15;
    $[43] = t16;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  return t22;
}